import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PROGRAM_KINDS, PROGRAM_KIND_LABELS } from '@affily/shared/constants';
import { formatCount, bpsToPercent, percentToBps } from '@affily/shared/money';
import { api } from '../../lib/api.js';
import { rule } from '../../lib/fieldRules.js';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { PitchSummary } from '../../components/PitchSummary.jsx';
import { TransparencyBadge } from '../../components/Transparency.jsx';
import { Button, Badge, Card, Field, Input, Textarea, Select, Spinner, EmptyState, Alert } from '../../components/ui.jsx';

const statusTone = {
  pending: 'caution',
  accepted: 'positive',
  declined: 'neutral',
  withdrawn: 'neutral',
};

const statusLabel = {
  pending: 'Waiting for an answer',
  accepted: 'Accepted',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
};

/**
 * Pitches, for either side.
 *
 * A creator sees the campaigns they suggested, with the brand's answer, and can
 * withdraw one still waiting. A brand sees the ones sent to the brand on screen,
 * and says yes (Affily sets it up as a private program and invites the creator)
 * or no, with a note. The brand also switches pitches on or off here.
 */
export default function Pitches({ side }) {
  const [params] = useSearchParams();
  const { data, loading, reload } = useFetch(`/pitches?side=${side}`);
  const [error, setError] = useState('');
  const pitches = data?.pitches ?? [];

  return (
    <DashboardLayout
      side={side}
      title="Pitches"
      description={
        side === 'brand'
          ? 'Campaigns creators have suggested to you, with their brief and conditions.'
          : 'Campaigns you suggested to brands, and their answers.'
      }
      actions={
        side === 'creator' && (
          <Button to="/programs" variant="outline" size="sm">
            Find a brand to pitch
          </Button>
        )
      }
    >
      <Alert className="mb-4">{error}</Alert>
      {side === 'creator' && params.get('sent') && (
        <Alert tone="positive" className="mb-4">
          Sent. You’ll get an email when the brand answers.
        </Alert>
      )}
      {side === 'brand' && data?.brand && <AcceptsPitches settings={data.brand} onSaved={reload} onError={setError} />}

      {loading ? (
        <Spinner />
      ) : pitches.length === 0 ? (
        <EmptyState
          title="No pitches yet"
          action={side === 'creator' && <Button to="/programs">Browse brands’ programs</Button>}
        >
          {side === 'brand'
            ? 'Creators can pitch you a campaign from your brand page.'
            : 'Open a brand’s page and press “Pitch a campaign” to suggest one.'}
        </EmptyState>
      ) : (
        <div className="space-y-4">
          {pitches.map((p) =>
            side === 'brand' ? (
              <IncomingPitch key={p.id} pitch={p} website={data?.brand?.website} onDone={reload} onError={setError} />
            ) : (
              <SentPitch key={p.id} pitch={p} onDone={reload} onError={setError} />
            ),
          )}
        </div>
      )}
    </DashboardLayout>
  );
}

function PitchHeader({ pitch, who }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="font-semibold">{pitch.title}</h2>
        <div className="mt-0.5 text-sm text-ink-muted">{who}</div>
      </div>
      <div className="text-right">
        <Badge tone={statusTone[pitch.status]}>{statusLabel[pitch.status]}</Badge>
        <span className="mt-0.5 block text-xs text-ink-faint">{new Date(pitch.createdAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

function DecisionNote({ pitch, from }) {
  if (!pitch.decisionNote) return null;
  return (
    <p className="mt-3 rounded-md border border-rule bg-paper-sunken px-3 py-2 text-sm">
      <span className="font-medium">{from}:</span> {pitch.decisionNote}
    </p>
  );
}

/** One of the creator's own pitches. */
function SentPitch({ pitch, onDone, onError }) {
  const [busy, setBusy] = useState(false);

  async function withdraw() {
    setBusy(true);
    onError('');
    try {
      await api.post(`/pitches/${pitch.id}/withdraw`);
      onDone();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-4 sm:p-5">
      <PitchHeader
        pitch={pitch}
        who={
          pitch.brand ? (
            <>
              To{' '}
              <Link to={`/brands/${pitch.brand.slug}`} className="hover:text-accent">
                {pitch.brand.companyName}
              </Link>
            </>
          ) : (
            'Brand removed'
          )
        }
      />
      <DecisionNote pitch={pitch} from={pitch.brand?.companyName ?? 'The brand'} />
      <details className="mt-3">
        <summary className="cursor-pointer text-sm text-accent">Your brief and conditions</summary>
        <div className="mt-3">
          <PitchSummary pitch={pitch} />
        </div>
      </details>
      <div className="mt-4 flex flex-wrap gap-2">
        {pitch.status === 'pending' && (
          <Button size="sm" variant="ghost" onClick={withdraw} disabled={busy}>
            Withdraw
          </Button>
        )}
        {pitch.status === 'accepted' && (
          <Button size="sm" to="/dashboard/creator/applications">
            See the invitation
          </Button>
        )}
        {pitch.brand && ['declined', 'withdrawn'].includes(pitch.status) && (
          <Button size="sm" variant="outline" to={`/dashboard/creator/pitches/new?brand=${pitch.brand.slug}`}>
            Send a new pitch
          </Button>
        )}
      </div>
    </Card>
  );
}

/** A pitch sent to the brand on screen, with its yes and no. */
function IncomingPitch({ pitch, website, onDone, onError }) {
  const [mode, setMode] = useState(null); // 'accept' | 'decline' | null
  const [accepted, setAccepted] = useState(null);
  const creator = pitch.creator;

  return (
    <Card className="p-4 sm:p-5">
      <PitchHeader
        pitch={pitch}
        who={
          creator ? (
            <span className="flex flex-wrap items-center gap-1.5">
              From{' '}
              <Link to={`/creators/${creator.slug}`} className="font-medium text-ink hover:text-accent">
                {creator.displayName}
              </Link>
              <span className="text-xs">· {formatCount(creator.totalFollowers)} followers</span>
              <TransparencyBadge transparency={creator.transparency} size="sm" showDetail={false} />
            </span>
          ) : (
            'Creator removed'
          )
        }
      />
      <DecisionNote pitch={pitch} from="Your note" />

      {pitch.status === 'pending' ? (
        <div className="mt-4">
          <PitchSummary pitch={pitch} />
        </div>
      ) : (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-accent">The brief and conditions</summary>
          <div className="mt-3">
            <PitchSummary pitch={pitch} />
          </div>
        </details>
      )}

      {accepted && (
        <Alert tone="positive" className="mt-4">
          Accepted. “{accepted.title}” is live as a private program, and {creator?.displayName ?? 'the creator'} has
          the invitation.{' '}
          <Link to={`/dashboard/brand/programs/${accepted.id}`} className="underline">
            Open the program
          </Link>
        </Alert>
      )}

      {pitch.status === 'accepted' && pitch.programId && !accepted && (
        <div className="mt-4">
          <Button size="sm" variant="outline" to={`/dashboard/brand/programs/${pitch.programId}`}>
            Open the program
          </Button>
        </div>
      )}

      {pitch.status === 'pending' && !mode && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setMode('accept')}>
            Accept…
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMode('decline')}>
            Decline…
          </Button>
        </div>
      )}
      {pitch.status === 'pending' && mode === 'accept' && (
        <AcceptForm
          pitch={pitch}
          website={website}
          onCancel={() => setMode(null)}
          onAccepted={(program) => {
            setAccepted(program);
            setMode(null);
            onDone();
          }}
          onError={onError}
        />
      )}
      {pitch.status === 'pending' && mode === 'decline' && (
        <DeclineForm pitch={pitch} onCancel={() => setMode(null)} onDone={onDone} onError={onError} />
      )}
    </Card>
  );
}

/**
 * Saying yes: what the program pays for (which sets Affily's fee), the amount
 * if the brand wants to change it, and where links send people.
 */
function AcceptForm({ pitch, website, onCancel, onAccepted, onError }) {
  const isPercent = pitch.commissionType === 'percent_of_sale';
  const isFlat = ['flat_per_post', 'flat_per_conversion'].includes(pitch.commissionType);
  const [form, setForm] = useState({
    kind: 'other',
    ratePercent: pitch.rateBps != null ? String(bpsToPercent(pitch.rateBps)) : '',
    flatAmount: pitch.flatAmountCents != null ? String(pitch.flatAmountCents / 100) : '',
    destinationUrl: '',
    note: '',
  });
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    onError('');
    setFieldErrors({});
    try {
      const { program } = await api.post(`/pitches/${pitch.id}/accept`, {
        kind: form.kind,
        destinationUrl: form.destinationUrl,
        note: form.note,
        ...(isPercent && form.ratePercent ? { rateBps: percentToBps(Number(form.ratePercent)) } : {}),
        ...(isFlat && form.flatAmount ? { flatAmountCents: Math.round(Number(form.flatAmount) * 100) } : {}),
      });
      onAccepted(program);
    } catch (err) {
      onError(err.message);
      setFieldErrors(err.details ?? {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-4 space-y-4 rounded-md border border-rule bg-paper-sunken p-4">
      <Field
        label="What does it pay for?"
        required
        error={fieldErrors.kind}
        hint="Sets Affily’s fee on it; see your Pricing page."
      >
        <Select value={form.kind} onChange={set('kind')}>
          {PROGRAM_KINDS.map((k) => (
            <option key={k} value={k}>
              {PROGRAM_KIND_LABELS[k]}
            </option>
          ))}
        </Select>
      </Field>

      {isPercent && (
        <Field label="% of each sale" error={fieldErrors.rateBps} constraint={rule.percent()} hint="Change it if you’d like to offer another rate.">
          <Input type="number" min="0.01" max="100" step="0.01" value={form.ratePercent} onChange={set('ratePercent')} />
        </Field>
      )}
      {isFlat && (
        <Field
          label={`Amount per ${pitch.commissionType === 'flat_per_post' ? 'post' : 'sale or lead'} (${pitch.currency.toUpperCase()})`}
          error={fieldErrors.flatAmountCents}
          constraint={rule.money()}
          hint="Change it if you’d like to offer another amount."
        >
          <Input type="number" min="1" step="0.01" value={form.flatAmount} onChange={set('flatAmount')} />
        </Field>
      )}

      <Field
        label="Where should links send people?"
        error={fieldErrors.destinationUrl}
        hint={website ? `Leave empty for ${website}` : 'Your website or a landing page'}
      >
        <Input type="url" value={form.destinationUrl} onChange={set('destinationUrl')} placeholder={website ?? 'https://'} required={!website} />
      </Field>

      <Field label="A note to the creator" hint="Optional; it goes with the invitation." error={fieldErrors.note}>
        <Textarea value={form.note} onChange={set('note')} maxLength={2000} rows={2} />
      </Field>

      <p className="text-xs text-ink-faint">
        Affily sets this up as a private program with these terms: only this creator sees them. They get an invitation
        and join when they accept it.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={busy}>
          {busy ? 'Accepting…' : 'Accept and invite'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function DeclineForm({ pitch, onCancel, onDone, onError }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    onError('');
    try {
      await api.post(`/pitches/${pitch.id}/decline`, { note });
      onDone();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-4 space-y-3 rounded-md border border-rule bg-paper-sunken p-4">
      <Field label="A note to the creator" hint="Optional, but it helps them pitch better next time.">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={2} />
      </Field>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" variant="danger" disabled={busy}>
          {busy ? 'Declining…' : 'Decline'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** The brand's "accept pitches" switch. */
function AcceptsPitches({ settings, onSaved, onError }) {
  const [busy, setBusy] = useState(false);

  async function toggle(e) {
    setBusy(true);
    onError('');
    try {
      await api.patch('/brands/me', { acceptsPitches: e.target.checked });
      onSaved();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <label className="mb-6 flex items-start gap-3 rounded-lg border border-rule bg-paper-raised px-4 py-3 text-sm">
      <input type="checkbox" className="mt-0.5" checked={settings.acceptsPitches} onChange={toggle} disabled={busy} />
      <span>
        <span className="font-medium">Accept pitches from creators</span>
        <span className="block text-ink-muted">
          {settings.acceptsPitches
            ? 'Creators see “Pitch a campaign” on your brand page.'
            : 'Off: creators can’t pitch you, but can still apply to your programs.'}
        </span>
      </span>
    </label>
  );
}
